import { useState } from 'react';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';

/**
 * Pre-set 16-colour palette for operator brand colours. Replaces the
 * raw hex TextFields — operators choose from a swatch grid instead of
 * typing six characters that have to be exactly right.
 *
 * "Custom hex" input still available behind a small pencil icon for
 * brand-strict cases where the operator needs an exact match. Empty
 * string means "fall back to ARMS default".
 */
export type BrandingPaletteProps = {
  /** Lower-case hex like "#1f3b6f", or "" for "use the ARMS default". */
  value: string;
  onChange: (next: string) => void;
  label: string;
  disabled?: boolean;
};

// 4×4 grid: cool / warm / neutral / accent. Tuned for legibility against
// both light and dark MUI themes. Swap any individual cell freely; the
// grid layout adapts.
export const PRESET_PALETTE = [
  '#1f3b6f', '#0e7c66', '#7d4f8f', '#c2410c',
  '#1d4ed8', '#16a34a', '#9333ea', '#dc2626',
  '#0f172a', '#334155', '#52525b', '#a16207',
  '#0891b2', '#65a30d', '#d6336c', '#f59e0b',
] as const;

const SWATCH_SIZE = 36;
const SWATCH_GAP  = 4;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function BrandingPalette({ value, onChange, label, disabled }: BrandingPaletteProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customDraft, setCustomDraft] = useState(value);
  const normalised = (value || '').toLowerCase();
  const inPalette = PRESET_PALETTE.some((p) => p.toLowerCase() === normalised);
  const customDraftOk = customDraft === '' || HEX_RE.test(customDraft);

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        {value && (
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {value}
          </Typography>
        )}
        {!value && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            <em>(ARMS default)</em>
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={showCustom ? 'Hide custom hex' : 'Use a custom hex value'}>
          <span>
            <IconButton size="small" onClick={() => setShowCustom((v) => !v)} disabled={disabled}>
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(4, ${SWATCH_SIZE}px)`,
        gap: `${SWATCH_GAP}px`,
        // Centre the small grid; the parent card is wider than the
        // palette so without this it would be left-stuck.
        width: 'fit-content',
      }}>
        {PRESET_PALETTE.map((hex) => {
          const selected = hex.toLowerCase() === normalised;
          return (
            <Tooltip key={hex} title={hex}>
              <Box
                role="button"
                aria-label={`Select colour ${hex}`}
                onClick={() => !disabled && onChange(hex)}
                sx={{
                  width: SWATCH_SIZE, height: SWATCH_SIZE,
                  bgcolor: hex,
                  borderRadius: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  // 2px white ring inside + 1px outer border = visible
                  // selection on any background.
                  boxShadow: selected
                    ? `inset 0 0 0 2px white, 0 0 0 2px ${hex}`
                    : 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                  opacity: disabled ? 0.5 : 1,
                  transition: 'transform 80ms',
                  '&:hover': disabled ? undefined : { transform: 'scale(1.06)' },
                }}
              >
                {selected && <CheckIcon sx={{ color: 'white', fontSize: 18 }} />}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {showCustom && (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Custom hex"
            placeholder="#1f3b6f"
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            error={!customDraftOk}
            helperText={!customDraftOk ? '#RRGGBB' : ''}
            disabled={disabled}
            sx={{ width: 140 }}
          />
          <Box
            sx={{
              width: SWATCH_SIZE, height: SWATCH_SIZE,
              bgcolor: customDraftOk && customDraft ? customDraft : 'transparent',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          />
          <Tooltip title="Apply custom hex">
            <span>
              <IconButton
                size="small" color="primary"
                disabled={disabled || !customDraftOk || customDraft === value}
                onClick={() => onChange(customDraft.toLowerCase())}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {/* Visual hint when the current value isn't in the preset grid.
          Keeps the user oriented so they realise they're on a custom
          hex, not "no swatch is selected because something is broken". */}
      {value && !inPalette && (
        <Typography variant="caption" color="text.secondary">
          Custom value — click any preset to switch.
        </Typography>
      )}
    </Stack>
  );
}
